import { BloodGlucoseUnitsLookup, Height, SerumCreatinineUnitHardCoded, TemperatureUnitsLookup, Weight } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { HeightUnitConverter } from "@/Common/Utilities/Units/HeightUnitConverter";
import { BloodGlucoseConcentrationUnit, HeightUnit, SerumCreatinineConcentrationUnit, TemperatureUnit, WeightUnit } from "@/Common/Utilities/Units/Units";
import { WeightUnitConverter } from "@/Common/Utilities/Units/WeightUnitConverter";
import { MedicalItem } from "./MedicalItem";

class MedicalItemVitalSign extends MedicalItem {
    public Weight: number;
    public Height: number;
    public BodySurface: number;
    public MassBodyIndex: number;
    public CreatinineClearance: number;
    public BPSys: number;
    public BPDia: number;
    public SerumCreatinine: number;
    public CreatinineUnit: string;
    public HeartRate: number;
    public O2Sat: number;
    public HeadCircumference: number;
    public WaistCircumference: number;
    public RespiratoryRate: number;
    public BPDiaStanding: number;
    public IsRegular: boolean;
    public BPSysStanding: number;
    public BPDiaLaid: number;
    public BPSysLaid: number;
    public Temperature: number;
    public BloodGlucose: number;
    public CreatinineClearanceAlgorythmLookupId: string;
    public CreatinineClearanceAlgorythmLookupValue: string;
    public HeadCircumferenceUnitLookupId: string;
    public HeadCircumferenceUnitLookupValue: string;
    public WaistCircumferenceUnitLookupId: string;
    public WaistCircumferenceUnitLookupValue: string;
    public TemperatureUnitLookupId: string;
    public TemperatureUnitLookupValue: string;
    public TemperatureTypeLookupId: string;
    public TemperatureTypeLookupValue: string;
    public BloodGlucoseUnitLookupId: string;
    public BloodGlucoseUnitLookupValue: string;
    public HeightUnitLookupId: string;
    public HeightUnitLookupValue: string;
    public WeightUnitLookupId: string;
    public WeightUnitLookupValue: string;
    public PainScaleValue: number;
    public Note: string;
    public IsLinkedToObstetricalFile: boolean;

    constructor(fields?: Partial<MedicalItemVitalSign>) {
        super(fields);
        Object.assign(this, fields);
    }

    public getHeightInInches(): number {
        if (!this.Height)
            return null;

        let lookupValue = cacheStore.GetLocalizedValueByLookupId(this.HeightUnitLookupId);
        return lookupValue === "in"
            ? this.Height
            : HeightUnitConverter.centimeterToInch(this.Height);
    }

    public getHeightInMeters(): number {
        if (!this.Height)
            return null;

        let lookupValue = cacheStore.GetLocalizedValueByLookupId(this.HeightUnitLookupId);
        return lookupValue === "in"
            ? HeightUnitConverter.inchToMeter(this.Height)
            : HeightUnitConverter.centimeterToMeter(this.Height);
    }

    public getHeightInCentimeters(): number {
        if (!this.Height)
            return null;

        const lookupValue = cacheStore.GetLocalizedValueByLookupId(this.HeightUnitLookupId);
        return lookupValue === "in"
            ? HeightUnitConverter.inchToCentimeter(this.Height)
            : this.Height;
    }

    public getWeightInKilograms(): number {
        if (!this.Weight)
            return null;

        let lookupValue = cacheStore.GetLocalizedValueByLookupId(this.WeightUnitLookupId);
        return lookupValue === "lb"
            ? WeightUnitConverter.poundToKilogram(this.Weight)
            : this.Weight;
    }

    public getWeightInGrams(): number {
        if (!this.Weight)
            return null;

        let lookUp = cacheStore.GetLookupById(this.WeightUnitLookupId);
        return lookUp?.LookupCode === Weight.Pound
            ? WeightUnitConverter.poundToGram(this.Weight)
            : lookUp?.LookupCode === Weight.Kilogram
                ? WeightUnitConverter.kilogramToGram(this.Weight)
                : null;
    }

    public getWeightInPounds(): number {
        if (!this.Weight)
            return null;

        let lookupValue = cacheStore.GetLocalizedValueByLookupId(this.WeightUnitLookupId);
        return lookupValue === "lb"
            ? this.Weight
            : WeightUnitConverter.kilogramToPound(this.Weight);
    }

    public getWeightIn(unit: WeightUnit): number {
        switch (unit) {
            case WeightUnit.Kilogram:
                return this.getWeightInKilograms();
            case WeightUnit.Pound:
                return this.getWeightInPounds();
            case WeightUnit.Gram:
                return this.getWeightInGrams();
        }
    }

    public getHeightIn(unit: HeightUnit): number {
        switch (unit) {
            case HeightUnit.Meter:
                return this.getHeightInMeters();
            case HeightUnit.Inch:
                return this.getHeightInInches();
            case HeightUnit.Centimeter:
                return this.getHeightInCentimeters();
        }
    }

    public getHeadCircumferenceInCentimeters(): number {
        if (!this.HeadCircumference)
            return null;

        const lookupValue = cacheStore.GetLocalizedValueByLookupId(this.HeadCircumferenceUnitLookupId);
        return lookupValue === "in"
            ? HeightUnitConverter.inchToCentimeter(this.HeadCircumference)
            : this.HeadCircumference;
    }

    public get HeightUnit(): HeightUnit {
        if (!this.HeightUnitLookupId)
            return null;

        const lookupCode = cacheStore.GetLookupById(this.HeightUnitLookupId)?.LookupCode;
        return lookupCode === Height.Inch
            ? HeightUnit.Inch
            : HeightUnit.Centimeter;
    }

    public get HeadCircumferenceUnit(): HeightUnit {
        if (!this.HeadCircumferenceUnitLookupId)
            return null;

        const lookupCode = cacheStore.GetLookupById(this.HeadCircumferenceUnitLookupId)?.LookupCode;
        return lookupCode === Height.Inch
            ? HeightUnit.Inch
            : HeightUnit.Centimeter;
    }

    public get WaistCircumferenceUnit(): HeightUnit {
        if (!this.WaistCircumferenceUnitLookupId)
            return null;

        const lookupCode = cacheStore.GetLookupById(this.WaistCircumferenceUnitLookupId)?.LookupCode;
        return lookupCode === Height.Inch
            ? HeightUnit.Inch
            : HeightUnit.Centimeter;
    }

    public get WeightUnit(): WeightUnit {
        if (!this.WeightUnitLookupId)
            return null;

        const lookupCode = cacheStore.GetLookupById(this.WeightUnitLookupId)?.LookupCode;
        return lookupCode === Weight.Pound
            ? WeightUnit.Pound
            : WeightUnit.Kilogram;
    }

    public get TemperatureUnit(): TemperatureUnit {
        if (!this.TemperatureUnitLookupId)
            return null;

        const lookupCode = cacheStore.GetLookupById(this.TemperatureUnitLookupId)?.LookupCode;
        return lookupCode === TemperatureUnitsLookup.Celcius
            ? TemperatureUnit.Celsius
            : TemperatureUnit.Fahrenheit;
    }

    public get SerumCreatinineUnit(): SerumCreatinineConcentrationUnit {
        if (!this.CreatinineUnit)
            return null;

        switch (this.CreatinineUnit) {
            case SerumCreatinineUnitHardCoded.MicromolesPerLitre:
                return SerumCreatinineConcentrationUnit.micromolesPerLitre;
            case SerumCreatinineUnitHardCoded.MilligramPer100Millilitres:
                return SerumCreatinineConcentrationUnit.milligramsPer100millilitre;
            default:
                return null;
        }
    }

    public get BloodGlucoseUnit(): BloodGlucoseConcentrationUnit {
        if (!this.BloodGlucoseUnitLookupId)
            return null;

        const lookupCode = cacheStore.GetLookupById(this.BloodGlucoseUnitLookupId)?.LookupCode;
        switch (lookupCode) {
            case BloodGlucoseUnitsLookup.millimolesPerLitres:
                return BloodGlucoseConcentrationUnit.millimolesPerLitre;
            case BloodGlucoseUnitsLookup.milligramsPerDecilitres:
                return BloodGlucoseConcentrationUnit.milligramPerDecilitre;
            default:
                return null;
        }
    }
}

export { MedicalItemVitalSign };
